/*
 * ErrorState: a failed load, with retry and the correlation id (so support can find the request).
 *   if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />
 * Title and text come from `errors:codes.<code>` when the problem code has a translation.
 */
import { ArrowClockwise, Check, Copy, WarningOctagon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { isApiError } from '@/api/client'
import { cn } from '@/lib/cn'
import { useCopy } from '@/lib/useCopy'
import { problemMessage } from '@/lib/errors'
import { Button } from './Button'

export function ErrorState({
  error,
  onRetry,
  title,
  compact,
  className,
}: {
  error?: unknown
  onRetry?: () => void
  title?: string
  compact?: boolean
  className?: string
}) {
  const { t } = useTranslation(['errors', 'common'])
  const [copied, copy] = useCopy()
  const cid = isApiError(error) ? error.correlationId : null
  const msg = problemMessage(error)
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center text-center',
        compact ? 'gap-2 px-4 py-8' : 'gap-3 px-6 py-14',
        className,
      )}
    >
      <span
        className="mb-1 grid size-12 place-items-center rounded-2xl bg-danger-50 text-danger-600 ring-1 ring-danger-100"
        aria-hidden="true"
      >
        <WarningOctagon className="size-6" weight="duotone" />
      </span>
      <h3 className="text-16 font-semibold text-ink">{title ?? msg.title}</h3>
      <p className="max-w-md text-14 leading-6 text-muted">{msg.detail}</p>
      <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
        {onRetry ? (
          <Button variant="secondary" size="sm" icon={<ArrowClockwise />} onClick={onRetry}>
            {t('common:actions.retry')}
          </Button>
        ) : null}
      </div>
      {cid ? (
        <button
          type="button"
          onClick={() => copy(cid)}
          className="mt-1 inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-12 text-subtle transition-colors hover:bg-control hover:text-ink"
        >
          {t('reference')}
          <bdi dir="ltr" className="font-mono">
            {cid.slice(0, 18)}
          </bdi>
          {copied ? (
            <Check className="size-3.5 text-teal-600" aria-hidden="true" />
          ) : (
            <Copy className="size-3.5" aria-hidden="true" />
          )}
          <span className="sr-only">{copied ? t('common:actions.copied') : t('common:actions.copy')}</span>
        </button>
      ) : null}
    </div>
  )
}
